/**
 * Universal Code Runner Service
 *
 * Provides a unified interface for executing code in different languages.
 * Delegates execution to specialized runners:
 * - Python: Pyodide (WASM)
 * - JavaScript: Function constructor (Sandbox)
 * - JSX: Babel/SWC Transpiler (via jsxRunner)
 * - HTML: Pass-through for Iframe
 */

import { runPythonCode, isPyodideReady } from './pyodide';

// Supported Languages
const LANG_PYTHON = 'python';
const LANG_JS = 'javascript';
const LANG_JSX = 'jsx';
const LANG_HTML = 'html';
const LANG_CSS = 'css';

/**
 * Executes code in the specified language.
 *
 * @param {string} language - The programming language (e.g., 'python', 'javascript')
 * @param {string} code - The source code to execute
 * @returns {Promise<{ stdout: string, variables: object, error: string }>}
 */
export async function executeCode(language, code) {
  const lang = (language || '').toLowerCase();

  switch (lang) {
    case LANG_PYTHON:
      return runPythonCode(code);

    case LANG_JS:
    case 'js':
      return runJavascriptCode(code);

    case LANG_JSX:
    case 'react':
      return runJsxCode(code);

    case LANG_HTML:
    case LANG_CSS:
    case 'htmlmixed':
      // HTML/CSS are treated as static output for iframe rendering
      return {
        stdout: code,
        variables: {},
        error: null
      };

    default:
      // Fallback: return code as stdout (useful for unknown langs displayed in terminal)
      return {
        stdout: code,
        variables: {},
        error: null // No error, just pass-through
      };
  }
}

/**
 * Runs JavaScript code in a lightweight sandbox with variable extraction.
 */
async function runJavascriptCode(code) {
  try {
    const logs = [];
    const errors = [];

    // Mock Console
    const sandboxConsole = {
      log: (...args) => logs.push(args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' ')),
      error: (...args) => errors.push(args.map(a => String(a)).join(' ')),
      warn: (...args) => logs.push('[WARN] ' + args.map(a => String(a)).join(' ')),
      info: (...args) => logs.push('[INFO] ' + args.map(a => String(a)).join(' ')),
    };

    // Variable extraction: wrap code so top-level let/const/var are captured
    // Convert let/const to var so they become properties of the function scope
    const wrappedCode = code
      .replace(/^\s*(?:let|const)\s+/gm, 'var ');

    // Create a scope object to capture variables
    const captureCode = `
      ${wrappedCode}
      var __captured = {};
      try {
        ${extractVarNames(code).map(v => `if (typeof ${v} !== 'undefined') __captured["${v}"] = ${v};`).join('\n        ')}
      } catch(e) {}
      return __captured;
    `;

    const sandboxFn = new Function('console', captureCode);
    const captured = sandboxFn(sandboxConsole) || {};

    let output = logs.join('\n');
    if (errors.length > 0) {
      output += '\n[Errors]\n' + errors.join('\n');
    }

    return {
      stdout: output || '(No output)',
      variables: captured,
      error: errors.length > 0 ? errors.join('\n') : null
    };
  } catch (e) {
    return {
      stdout: '',
      variables: {},
      error: `[Runtime Error] ${e.name}: ${e.message}`
    };
  }
}

/**
 * Extracts variable names from JS code (let/const/var declarations).
 */
function extractVarNames(code) {
  const names = new Set();
  const re = /(?:let|const|var)\s+([a-zA-Z_$][a-zA-Z0-9_$]*)/g;
  let m;
  while ((m = re.exec(code)) !== null) {
    names.add(m[1]);
  }
  return [...names];
}

/**
 * Runs JSX code by transpiling it to HTML.
 */
async function runJsxCode(code) {
  try {
    // Dynamic import to avoid bundling if not used
    const { runJSX } = await import('./jsxRunner');
    const result = await runJSX(code);

    if (result.error) {
      return {
        stdout: '',
        variables: {},
        error: `[JSX Error] ${result.error}`
      };
    }

    // Wrap in standard HTML for preview
    const htmlOutput = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body { margin: 0; padding: 16px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #333; }
  </style>
</head>
<body>${result.html}</body>
</html>`;

    return {
      stdout: htmlOutput,
      variables: {},
      error: null
    };
  } catch (e) {
    return {
      stdout: '',
      variables: {},
      error: `[System Error] ${e.message}`
    };
  }
}

/**
 * Checks if the runner for the given language is ready.
 */
export function isRunnerReady(language) {
  const lang = (language || '').toLowerCase();
  if (lang === LANG_PYTHON) {
    return isPyodideReady();
  }
  return true; // JS/JSX/HTML runners are always ready (or load quickly on demand)
}
